import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Badge, Spinner, Alert, Button, Row, Col, Table, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faCheckCircle, faTimesCircle, faExclamationTriangle, faPlayCircle, faRedo, faBolt } from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../utils/setupAxios';
import {
  ApiVersionBadge, EndpointBadge, ErrorBadge, StatusBadge, AiEstimatedBadge, isAiEstimatedResult,
} from './taskBadges';
import {
  CLIENT_DEVICE_INFO_FIELDS,
  getDeviceInfoFieldValue,
  hasClientDeviceContext,
} from './clientDeviceInfo';
import CollapsibleJsonDetail, { parseJsonValue } from './CollapsibleJsonDetail';

const STAGE_CONFIG = {
  started: { color: '#6c757d', bg: 'secondary', icon: faPlayCircle, label: 'Started' },
  primary_recognize: { color: '#0dcaf0', bg: 'info', icon: faRedo, label: 'Primary Recognize' },
  server_decode: { color: '#6610f2', bg: 'primary', icon: faRedo, label: 'Server Decode (V3)' },
  server_cms_enrich: { color: '#0dcaf0', bg: 'info', icon: faRedo, label: 'CMS Enrich' },
  primary_stream: { color: '#0dcaf0', bg: 'info', icon: faRedo, label: 'Primary Stream' },
  primary_attempt: { color: '#0dcaf0', bg: 'info', icon: faRedo, label: 'Primary Attempt' },
  fallback_triggered: { color: '#ffc107', bg: 'warning', icon: faExclamationTriangle, label: 'Fallback Triggered' },
  backup_recognize: { color: '#0d6efd', bg: 'primary', icon: faBolt, label: 'Backup Recognize' },
  backup_stream: { color: '#0d6efd', bg: 'primary', icon: faBolt, label: 'Backup Stream' },
  backup_attempt: { color: '#0d6efd', bg: 'primary', icon: faBolt, label: 'Backup Attempt' },
  completed: { color: '#198754', bg: 'success', icon: faCheckCircle, label: 'Completed' },
  failed: { color: '#dc3545', bg: 'danger', icon: faTimesCircle, label: 'Failed' },
};

const RESULT_ISSUE_LABELS = {
  ref_img_front_missing_in_result: 'Result missing ref_img_front (user front upload exists)',
  ref_img_back_missing_in_result: 'Result missing ref_img_back (user back upload exists)',
  denomination_side_url_missing_in_result: 'grade_info_data.denomination_side_url is empty',
  subject_side_url_missing_in_result: 'grade_info_data.subject_side_url is empty',
  price_title_empty: 'price_title is empty',
};

const ACTION_COLORS = {
  retry: 'warning',
  fallback: 'warning',
  completed: 'success',
  success: 'success',
  retry_exceeded: 'danger',
};

const CoinTaskDetail = () => {
  const { taskId } = useParams();
  const navigate = useNavigate();

  const [task, setTask] = useState(null);
  const [events, setEvents] = useState([]);
  const [iterations, setIterations] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [taskIdCopied, setTaskIdCopied] = useState(false);
  const taskIdCopyTimerRef = useRef(null);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      setError(null);
      try {
        const [taskRes, eventsRes] = await Promise.all([
          axiosInstance.get(`/holo/tasks/${taskId}`),
          axiosInstance.get(`/holo/tasks/${taskId}/events`),
        ]);

        if (taskRes.data.success) {
          setTask(taskRes.data.data);
        } else {
          setError(taskRes.data.message || 'Task not found');
        }

        if (eventsRes.data.success) {
          setEvents(eventsRes.data.data.events || []);
          setIterations(eventsRes.data.data.iterations || {});
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load task');
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [taskId]);

  useEffect(() => {
    return () => {
      if (taskIdCopyTimerRef.current) {
        clearTimeout(taskIdCopyTimerRef.current);
        taskIdCopyTimerRef.current = null;
      }
    };
  }, []);

  const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString();
  };

  const formatMs = (ms) => {
    if (ms == null) return '—';
    if (ms >= 1000) return `${(ms / 1000).toFixed(2)}s`;
    return `${ms}ms`;
  };

  const taskIsVpn = (t) => Boolean(t?.attributes?.is_vpn);

  const copyTaskId = async () => {
    const text = task?.task_id;
    if (!text) return;

    try {
      if (typeof navigator?.clipboard?.writeText === 'function') {
        await navigator.clipboard.writeText(text);
        setTaskIdCopied(true);
        if (taskIdCopyTimerRef.current) clearTimeout(taskIdCopyTimerRef.current);
        taskIdCopyTimerRef.current = setTimeout(() => setTaskIdCopied(false), 1500);
      }
    } catch (_) {
      // ignore (clipboard might be blocked)
    }
  };

  const hasClientReportContext = (t) => Boolean(
    t.client_reported
    || t.client_error_message
    || t.client_report_tag
    || t.client_reason
    || t.client_device_info
    || t.client_device_id
    || t.client_country_code
  );

  const InfoRow = ({ label, value }) => (
    <tr>
      <td className="text-muted fw-semibold task-detail__label">{label}</td>
      <td className="task-detail__value">{value ?? '—'}</td>
    </tr>
  );

  const getUploadImageLabels = (endpoint) => {
    if (endpoint === 'stream_message') {
      return { front: 'Denomination side', back: 'Subject side' };
    }
    return { front: 'Front', back: 'Back' };
  };

  const renderUploadedImages = () => {
    const { image_front_url: frontUrl, image_back_url: backUrl, endpoint } = task;
    if (!frontUrl && !backUrl) return null;

    const labels = getUploadImageLabels(endpoint);
    const imageStyle = { maxWidth: '100%', maxHeight: 320, borderRadius: 8, objectFit: 'contain' };

    const renderImage = (url, label) => (
      <Col md={6} key={label}>
        <div className="text-muted small fw-semibold mb-2">{label}</div>
        {url ? (
          <a href={url} target="_blank" rel="noopener noreferrer">
            <img alt={label} src={url} style={imageStyle} />
          </a>
        ) : (
          <span className="text-muted">—</span>
        )}
      </Col>
    );

    return (
      <Card className="mb-4">
        <Card.Header>
          <strong>Uploaded Images</strong>
        </Card.Header>
        <Card.Body>
          <Row className="g-4">
            {renderImage(frontUrl, labels.front)}
            {renderImage(backUrl, labels.back)}
          </Row>
        </Card.Body>
      </Card>
    );
  };

  const formatPrice = (p) => (p == null || p === '' ? '—' : `$${p}`);

  const formatConfidence = (n) => {
    if (typeof n === 'number') return `${(n * 100).toFixed(1)}%`;
    if (typeof n === 'string' && n !== '') return n;
    return '—';
  };

  const CONDITION_COLORS = {
    Mint: '#198754',
    'Near Mint': '#20c997',
    'Lightly Played': '#0dcaf0',
    'Moderately Played': '#ffc107',
    'Heavily Played': '#fd7e14',
    Damaged: '#dc3545',
  };

  const renderPriceEstimateResult = (result, response) => {
    const { card, price_estimate: priceEstimate, raw_identify_result: gpt } = response;
    const resultOk = result.result === 1 || result.message === 'success';

    return (
      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <strong>Recognition Result</strong>
          <div className="d-flex align-items-center gap-2">
            {result.component && (
              <Badge style={{ backgroundColor: '#6c757d', color: '#fff' }}>{result.component}</Badge>
            )}
            <AiEstimatedBadge />
            <Badge bg={resultOk ? 'success' : 'danger'} text={resultOk ? undefined : 'white'}>
              {result.message || (resultOk ? 'success' : 'failed')}
            </Badge>
          </div>
        </Card.Header>
        <Card.Body>
          <div className="d-flex align-items-center flex-wrap gap-2 mb-2">
            <h4 className="mb-0">{card?.card_name || '—'}</h4>
            {card?.rarity && <Badge bg="warning" text="dark">{card.rarity}</Badge>}
            {card?.variant_type && (
              <Badge style={{ backgroundColor: '#6c757d', color: '#fff' }}>{card.variant_type}</Badge>
            )}
          </div>

          {priceEstimate && (
            <div className="fs-4 fw-bold text-success mb-3">
              {formatPrice(priceEstimate.from_price)} – {formatPrice(priceEstimate.to_price)}
              {priceEstimate.price_unit && (
                <span className="fs-6 text-muted fw-normal ms-2">{priceEstimate.price_unit}</span>
              )}
              {priceEstimate.confidence && (
                <Badge bg="light" text="dark" className="ms-2 align-middle">{priceEstimate.confidence} confidence</Badge>
              )}
            </div>
          )}

          <Table size="sm" className="mb-0 table-borderless">
            <tbody>
              <InfoRow label="Game" value={card?.game_name} />
              <InfoRow label="Series" value={card?.series_name} />
              <InfoRow label="Card Number" value={card?.card_number} />
              <InfoRow
                label="Card Name (original)"
                value={card?.card_name_original && card.card_name_original !== card.card_name
                  ? card.card_name_original
                  : null}
              />
              <InfoRow label="Condition (AI đoán)" value={card?.condition_estimate} />
              <InfoRow label="Language" value={card?.language} />
            </tbody>
          </Table>

          {priceEstimate?.price_description && (
            <div className="mt-3 pt-3 border-top">
              <div className="text-muted fw-semibold small mb-1">Price description</div>
              <p className="mb-0 small">{priceEstimate.price_description}</p>
            </div>
          )}

          {Array.isArray(priceEstimate?.factors) && priceEstimate.factors.length > 0 && (
            <div className="mt-3 pt-3 border-top">
              <div className="text-muted fw-semibold small mb-2">Factors</div>
              <div className="d-flex flex-wrap gap-2">
                {priceEstimate.factors.map((factor) => (
                  <Badge key={factor} style={{ backgroundColor: '#6c757d', color: '#fff' }}>{factor}</Badge>
                ))}
              </div>
            </div>
          )}

          {gpt && (
            <div className="mt-3 pt-3 border-top text-muted small">
              model: <code className="task-detail__code">{gpt.model || '—'}</code>
              {gpt.source && <span className="ms-2">source: {gpt.source}</span>}
            </div>
          )}

          <div className="mt-3 pt-3 border-top">
            <CollapsibleJsonDetail label="Full result (raw)" value={result} />
          </div>
        </Card.Body>
      </Card>
    );
  };

  const renderRecognitionResult = () => {
    const result = parseJsonValue(task.stored_result);
    if (!result || typeof result !== 'object') return null;

    const response = result.response;
    const resultOk = result.result === 1 || result.message === 'success';

    // No structured response (e.g. failed/decrypt issue) — still surface the raw payload.
    if (!response || typeof response !== 'object') {
      return (
        <Card className="mb-4">
          <Card.Header><strong>Recognition Result</strong></Card.Header>
          <Card.Body>
            <CollapsibleJsonDetail label="Stored result (raw)" value={result} />
          </Card.Body>
        </Card>
      );
    }

    // estimate_price responses use a distinct, flatter shape ({card, price_estimate, raw_identify_result})
    // instead of the recognize/recognize_backup shape (game/series/card/sku/sku_list/...).
    if (response.price_estimate && typeof response.price_estimate === 'object') {
      return renderPriceEstimateResult(result, response);
    }

    const { game, series, card, sku, sku_list: skuList, vlm_query_result: vlm, engine_identify_result: engine } = response;
    const aiEstimated = isAiEstimatedResult(response);

    // Matched reference image + similarity come from the engine's raw_result JSON string.
    const engineRaw = parseJsonValue(engine?.raw_result);
    const matchedImageUrl = (engineRaw && typeof engineRaw === 'object') ? engineRaw.url : null;
    const similarity = engine?.similarity ?? (engineRaw && typeof engineRaw === 'object' ? engineRaw.similarity : null);

    const imageStyle = {
      maxWidth: '100%',
      maxHeight: 260,
      borderRadius: 8,
      objectFit: 'contain',
      border: '1px solid #dee2e6',
    };

    const renderImg = (url, label) => (
      <div className="mb-3">
        <div className="text-muted small fw-semibold mb-1">{label}</div>
        {url ? (
          <a href={url} target="_blank" rel="noopener noreferrer">
            <img src={url} alt={label} style={imageStyle} />
          </a>
        ) : (
          <span className="text-muted">—</span>
        )}
      </div>
    );

    return (
      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <strong>Recognition Result</strong>
          <div className="d-flex align-items-center gap-2">
            {result.component && (
              <Badge style={{ backgroundColor: '#6c757d', color: '#fff' }}>{result.component}</Badge>
            )}
            {aiEstimated && <AiEstimatedBadge />}
            <Badge bg={resultOk ? 'success' : 'danger'} text={resultOk ? undefined : 'white'}>
              {result.message || (resultOk ? 'success' : 'failed')}
            </Badge>
          </div>
        </Card.Header>
        <Card.Body>
          <Row className="g-4">
            <Col md={4}>
              {renderImg(task.image_url, 'Uploaded image')}
              {renderImg(matchedImageUrl, 'Matched reference')}
            </Col>
            <Col md={8}>
              <div className="d-flex align-items-center flex-wrap gap-2 mb-2">
                <h4 className="mb-0">{card?.name || '—'}</h4>
                {card?.rarity_full_name && (
                  <Badge bg="warning" text="dark">{card.rarity_full_name}</Badge>
                )}
              </div>
              {sku?.market_price && (
                <div className="fs-4 fw-bold text-success mb-3">
                  {formatPrice(sku.market_price)}
                  {sku.variant && <span className="fs-6 text-muted fw-normal ms-2">{sku.variant}</span>}
                </div>
              )}
              <Table size="sm" className="mb-0 table-borderless">
                <tbody>
                  <InfoRow label="Game" value={game?.name} />
                  <InfoRow
                    label="Series"
                    value={series?.name ? (
                      <span>
                        {series.name}
                        {series.card_count != null && (
                          <span className="text-muted small ms-2">({series.card_count} cards)</span>
                        )}
                      </span>
                    ) : null}
                  />
                  <InfoRow label="Card Number" value={card?.number_nr} />
                  <InfoRow label="Class" value={card?.character_class} />
                  <InfoRow label="Artist" value={card?.artist} />
                  <InfoRow
                    label="TCGPlayer ID"
                    value={card?.tcgplayer_id ? <code className="small">{card.tcgplayer_id}</code> : null}
                  />
                  <InfoRow label="Condition" value={sku?.condition} />
                  <InfoRow label="Language" value={sku?.language} />
                  {similarity != null && (
                    <InfoRow label="Engine similarity" value={formatConfidence(similarity)} />
                  )}
                </tbody>
              </Table>
            </Col>
          </Row>

          {card?.description && (
            <div className="mt-3 pt-3 border-top">
              <div className="text-muted fw-semibold small mb-1">Description</div>
              <pre className="mb-0 small task-detail__text-break" style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
                {card.description}
              </pre>
            </div>
          )}

          {Array.isArray(skuList) && skuList.length > 0 && (
            <div className="mt-3 pt-3 border-top">
              <div className="text-muted fw-semibold small mb-2">Price by condition ({skuList.length})</div>
              <Table size="sm" hover responsive className="mb-0 align-middle">
                <thead>
                  <tr className="text-muted small">
                    <th>Condition</th>
                    <th>Variant</th>
                    <th>Language</th>
                    <th className="text-end">Market price</th>
                  </tr>
                </thead>
                <tbody>
                  {skuList.map((s) => {
                    const isSelected = sku && s.uid === sku.uid;
                    return (
                      <tr key={s.uid || s.id} className={isSelected ? 'table-success' : undefined}>
                        <td>
                          <span
                            className="d-inline-block rounded-circle me-2"
                            style={{ width: 10, height: 10, backgroundColor: CONDITION_COLORS[s.condition] || '#6c757d' }}
                          />
                          {s.condition}
                          {isSelected && <Badge bg="success" className="ms-2">selected</Badge>}
                        </td>
                        <td>{s.variant}</td>
                        <td>{s.language}</td>
                        <td className="text-end fw-semibold">{formatPrice(s.market_price)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}

          {vlm && typeof vlm === 'object' && (
            <div className="mt-3 pt-3 border-top">
              <div className="text-muted fw-semibold small mb-2">VLM query</div>
              <div className="d-flex flex-wrap gap-3 small">
                <span>
                  <span className="text-muted">Name: </span>
                  {vlm.card_name || '—'}
                  {vlm.card_name_original && vlm.card_name_original !== vlm.card_name && (
                    <span className="text-muted"> ({vlm.card_name_original})</span>
                  )}
                  {vlm.card_name_confidence && (
                    <Badge bg="light" text="dark" className="ms-1">{vlm.card_name_confidence}</Badge>
                  )}
                </span>
                <span>
                  <span className="text-muted">Language: </span>
                  {vlm.language || '—'}
                  {vlm.language_confidence && (
                    <Badge bg="light" text="dark" className="ms-1">{vlm.language_confidence}</Badge>
                  )}
                </span>
              </div>
            </div>
          )}

          <div className="mt-3 pt-3 border-top">
            <CollapsibleJsonDetail label="Full result (raw)" value={result} />
          </div>
        </Card.Body>
      </Card>
    );
  };

  const renderTaskInfo = () => {
    const storedResult = parseJsonValue(task.stored_result);
    const resultResponse = storedResult && typeof storedResult === 'object' ? storedResult.response : null;
    const aiEstimated = isAiEstimatedResult(resultResponse);

    return (
    <Card className="mb-4">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <strong>Task Info</strong>
        <StatusBadge status={task.status} style={{ fontSize: '0.85rem', padding: '0.4rem 0.7rem' }} />
      </Card.Header>
      <Card.Body className="p-0">
        <Row className="g-0">
          <Col md={6}>
            <Table size="sm" className="mb-0 table-borderless" style={{ borderRight: '1px solid #dee2e6' }}>
              <tbody>
                <InfoRow
                  label="Task ID"
                  value={(
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip>{taskIdCopied ? 'Copied' : 'Click to copy'}</Tooltip>}
                    >
                      <code
                        role="button"
                        tabIndex={0}
                        onClick={copyTaskId}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') copyTaskId();
                        }}
                        style={{ cursor: 'pointer' }}
                        title="Click to copy"
                      >
                        {task.task_id}
                      </code>
                    </OverlayTrigger>
                  )}
                />
                <InfoRow label="Endpoint" value={<EndpointBadge endpoint={task.endpoint} />} />
                <InfoRow
                  label="Nguồn kết quả"
                  value={aiEstimated
                    ? <AiEstimatedBadge>AI Estimated (OpenAI)</AiEstimatedBadge>
                    : <Badge style={{ backgroundColor: '#6c757d', color: '#fff' }}>Vendor chính</Badge>}
                />
                <InfoRow label="API Version" value={<ApiVersionBadge task={task} />} />
                {task.recognition_mode && (
                  <InfoRow label="Recognition Mode" value={task.recognition_mode} />
                )}
                <InfoRow label="Platform" value={task.platform} />
                <InfoRow label="Primary Vendor" value={task.primary_vendor} />
                <InfoRow label="Final Vendor" value={task.final_vendor} />
                <InfoRow
                  label="VPN (client)"
                  value={taskIsVpn(task) ? (
                    <div>
                      <Badge bg="info" text="dark" className="me-2">Yes</Badge>
                      <span className="text-muted small">
                        Mobile reported VPN — backup vendor was selected instead of primary.
                      </span>
                    </div>
                  ) : (
                    <Badge style={{ backgroundColor: '#6c757d', color: '#fff' }}>No</Badge>
                  )}
                />
                <InfoRow label="Fallback" value={
                  task.fallback_used
                    ? <Badge bg="warning" text="dark">Yes — {task.fallback_reason || 'unknown'}</Badge>
                    : <Badge style={{ backgroundColor: '#6c757d', color: '#fff' }}>No</Badge>
                } />
              </tbody>
            </Table>
          </Col>
          <Col md={6}>
            <Table size="sm" className="mb-0 table-borderless">
              <tbody>
                <InfoRow label="Primary Attempts" value={task.primary_attempts} />
                <InfoRow label="Backup Attempts" value={task.backup_attempts} />
                <InfoRow label="Primary Duration" value={formatMs(task.primary_duration_ms)} />
                <InfoRow label="Backup Duration" value={formatMs(task.backup_duration_ms)} />
                <InfoRow label="Total Duration" value={formatMs(task.total_duration_ms)} />
                <InfoRow label="Primary Final HTTP" value={task.primary_final_status_code} />
                <InfoRow label="Primary Final Error" value={task.primary_final_error} />
                <InfoRow label="Event Count" value={task.event_count} />
                <InfoRow label="Created" value={formatDate(task.created_at)} />
                <InfoRow label="Updated" value={formatDate(task.updated_at)} />
              </tbody>
            </Table>
          </Col>
        </Row>
      </Card.Body>
    </Card>
    );
  };

  const renderClientDeviceSection = () => {
    if (!hasClientDeviceContext(task)) return null;

    const deviceInfo = parseJsonValue(task.client_device_info);
    const rows = CLIENT_DEVICE_INFO_FIELDS.map(({ key, label }) => {
      let value = getDeviceInfoFieldValue(deviceInfo, key);
      if (key === 'device_id' && !value && task.client_device_id) {
        value = task.client_device_id;
      }
      if (key === 'country_code' && !value && task.client_country_code) {
        value = task.client_country_code;
      }
      if (key === 'app_version' && !value && task.app_version) {
        value = task.app_version;
      }
      return { key, label, value };
    }).filter((row) => row.value != null);

    if (rows.length === 0 && !deviceInfo) return null;

    const midpoint = Math.ceil(rows.length / 2);
    const leftRows = rows.slice(0, midpoint);
    const rightRows = rows.slice(midpoint);

    const renderColumn = (columnRows) => (
      <Table size="sm" className="mb-0 table-borderless">
        <tbody>
          {columnRows.map((row) => (
            <InfoRow
              key={row.key}
              label={row.label}
              value={row.key === 'email' || row.key === 'device_id' || row.key === 'rc_app_user_id'
                ? <code className="small">{row.value}</code>
                : row.value}
            />
          ))}
        </tbody>
      </Table>
    );

    return (
      <Card className="mb-4">
        <Card.Header>
          <strong>Client Device Info</strong>
        </Card.Header>
        <Card.Body className="p-0">
          {rows.length > 0 ? (
            <Row className="g-0">
              <Col md={6} style={{ borderRight: '1px solid #dee2e6' }}>
                <div className="p-0">{renderColumn(leftRows)}</div>
              </Col>
              <Col md={6}>
                <div className="p-0">{renderColumn(rightRows)}</div>
              </Col>
            </Row>
          ) : (
            <div className="px-3 py-3">
              <CollapsibleJsonDetail label="Device info (raw)" value={task.client_device_info} />
            </div>
          )}
        </Card.Body>
      </Card>
    );
  };

  const renderResultDiagnostics = () => {
    const diagnostics = task.result_diagnostics;
    if (!diagnostics) return null;

    const issues = Array.isArray(diagnostics.issues) ? diagnostics.issues : [];
    const hasBackupContext = diagnostics.final_vendor === 'backup'
      || diagnostics.backup_openai_preview
      || diagnostics.backup_error_code;

    const hasStreamText = Boolean(diagnostics.stream_result_text);
    const resultRefLabel = String(diagnostics.result_source || '').startsWith('stream_result') ? 'stream result' : 'stored result';

    if (!hasBackupContext && issues.length === 0 && !hasStreamText) {
      return null;
    }

    return (
      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <strong>Recognition Result Diagnostics</strong>
          {issues.length > 0 ? (
            <Badge bg="warning" text="dark">{issues.length} issue{issues.length > 1 ? 's' : ''}</Badge>
          ) : (
            <Badge bg="success">OK</Badge>
          )}
        </Card.Header>
        <Card.Body className="p-0">
          <Table size="sm" className="mb-0 table-borderless">
            <tbody>
              <InfoRow label="Final Vendor" value={diagnostics.final_vendor} />
              {diagnostics.result_source && (
                <InfoRow
                  label="Result source"
                  value={<Badge style={{ backgroundColor: '#6c757d', color: '#fff' }}>{diagnostics.result_source}</Badge>}
                />
              )}
              <InfoRow
                label="User upload → ref_img_front"
                value={(
                  <div className="small">
                    <div className="text-muted">upload</div>
                    <code className="task-detail__code">{diagnostics.upload_image_front_url || '—'}</code>
                    <div className="text-muted mt-1">{resultRefLabel}</div>
                    <code className="task-detail__code">{diagnostics.result_ref_img_front || '—'}</code>
                  </div>
                )}
              />
              <InfoRow
                label="User upload → ref_img_back"
                value={(
                  <div className="small">
                    <div className="text-muted">upload</div>
                    <code className="task-detail__code">{diagnostics.upload_image_back_url || '—'}</code>
                    <div className="text-muted mt-1">{resultRefLabel}</div>
                    <code className="task-detail__code">{diagnostics.result_ref_img_back || '—'}</code>
                  </div>
                )}
              />
              <InfoRow label="Result price_title" value={diagnostics.result_price_title} />
              <InfoRow label="Result cms_name_uids_with_year" value={diagnostics.result_cms_name_uids_with_year} />
              {hasStreamText && (
                <InfoRow
                  label="Stream result"
                  value={(
                    <pre className="mb-0 small task-detail__text-break" style={{ whiteSpace: 'pre-wrap' }}>
                      {String(diagnostics.stream_result_text)}
                    </pre>
                  )}
                />
              )}
              {diagnostics.backup_model && (
                <InfoRow label="Backup model" value={<code>{diagnostics.backup_model}</code>} />
              )}
              {diagnostics.backup_http_status != null && (
                <InfoRow label="Backup HTTP" value={diagnostics.backup_http_status} />
              )}
              {diagnostics.backup_error_code && (
                <InfoRow label="Backup event error" value={<ErrorBadge>{diagnostics.backup_error_code}</ErrorBadge>} />
              )}
            </tbody>
          </Table>

          {issues.length > 0 && (
            <div className="px-3 pb-3 border-top">
              <div className="text-muted fw-semibold small mb-2 pt-2">Issues</div>
              <ul className="mb-0 small">
                {issues.map((issue) => (
                  <li key={issue} className="text-danger">
                    {RESULT_ISSUE_LABELS[issue] || issue}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {(diagnostics.backup_openai_metadata || diagnostics.backup_openai_preview) && (
            <div className="px-3 pb-3 border-top pt-2 d-flex flex-column gap-3">
              <CollapsibleJsonDetail
                label="OpenAI backup metadata"
                value={diagnostics.backup_openai_metadata}
              />
              <CollapsibleJsonDetail
                label="OpenAI backup raw JSON preview"
                value={diagnostics.backup_openai_preview}
              />
            </div>
          )}
        </Card.Body>
      </Card>
    );
  };

  const renderErrorSection = () => {
    const showSection = task.status === 'failed' || task.error_code || hasClientReportContext(task);
    if (!showSection) return null;

    const reason = parseJsonValue(task.client_reason);

    return (
      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <strong>Error &amp; Client Report</strong>
          {task.client_reported && (
            <Badge bg="danger" text="white">Client reported</Badge>
          )}
        </Card.Header>
        <Card.Body className="p-0">
          <Table size="sm" className="mb-0 table-borderless">
            <tbody>
              <InfoRow
                label="Error Code"
                value={task.error_code ? <ErrorBadge>{task.error_code}</ErrorBadge> : null}
              />
              <InfoRow label="Error Origin" value={task.error_origin} />
              <InfoRow label="Error Action Group" value={task.error_action_group} />
              <InfoRow label="Client Final Status" value={task.client_final_status} />
              <InfoRow
                label="Error Message"
                value={task.client_error_message || reason?.message || null}
              />
              <InfoRow label="Tag" value={task.client_report_tag} />
            </tbody>
          </Table>
          {(reason || task.client_reason) && (
            <div className="px-3 pb-3 border-top pt-2">
              <CollapsibleJsonDetail label="Reason" value={reason ?? task.client_reason} />
            </div>
          )}
        </Card.Body>
      </Card>
    );
  };

  const renderIterations = () => {
    const entries = Object.entries(iterations);
    if (entries.length === 0) return null;
    return (
      <div className="d-flex gap-3 mb-4 flex-wrap">
        {entries.map(([vendor, count]) => (
          <Card key={vendor} className="text-center" style={{ minWidth: '140px' }}>
            <Card.Body className="py-2 px-3">
              <div className="text-muted small">{vendor}</div>
              <div className="fw-bold fs-4">{count}</div>
              <div className="text-muted small">attempts</div>
            </Card.Body>
          </Card>
        ))}
      </div>
    );
  };

  const renderEventsTimeline = () => {
    if (events.length === 0) return <p className="text-muted">No events found.</p>;

    return (
      <div style={{ position: 'relative' }}>
        {/* vertical line */}
        <div style={{
          position: 'absolute',
          left: '20px',
          top: '12px',
          bottom: '12px',
          width: '2px',
          background: '#dee2e6',
          zIndex: 0,
        }} />

        {events.map((event, idx) => {
          const cfg = STAGE_CONFIG[event.stage] || { color: '#6c757d', bg: 'secondary', icon: faRedo, label: event.stage };
          const isLast = idx === events.length - 1;

          return (
            <div key={event.id} style={{ display: 'flex', gap: '16px', marginBottom: isLast ? 0 : '16px', position: 'relative', zIndex: 1 }}>
              {/* dot */}
              <div style={{
                width: '42px',
                flexShrink: 0,
                display: 'flex',
                justifyContent: 'center',
                paddingTop: '10px',
              }}>
                <div style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  background: cfg.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontSize: '12px',
                  boxShadow: '0 0 0 3px #fff',
                }}>
                  <FontAwesomeIcon icon={cfg.icon} />
                </div>
              </div>

              {/* card */}
              <div className="task-event__content">
                <Card style={{ borderLeft: `3px solid ${cfg.color}` }}>
                  <Card.Body className="py-2 px-3">
                    <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-1">
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        <Badge bg={cfg.bg} className={['warning'].includes(cfg.bg) ? 'text-dark' : 'text-white'}>
                          {cfg.label}
                        </Badge>
                        {event.attempt != null && (
                          <span className="text-muted small">attempt #{event.attempt}</span>
                        )}
                        {event.vendor_name && (
                          <Badge style={{ backgroundColor: '#6c757d', color: '#fff' }}>{event.vendor_name}</Badge>
                        )}
                        {event.action_group && (() => {
                          const ac = ACTION_COLORS[event.action_group?.toLowerCase()];
                          if (ac === 'warning') return <Badge bg="warning" text="dark">{event.action_group}</Badge>;
                          if (ac) return <Badge bg={ac} text={ac === 'danger' ? 'white' : undefined}>{event.action_group}</Badge>;
                          return <Badge style={{ backgroundColor: '#6c757d', color: '#fff' }}>{event.action_group}</Badge>;
                        })()}
                      </div>
                      <span className="text-muted small" style={{ whiteSpace: 'nowrap' }}>
                        {formatDate(event.server_ts)}
                      </span>
                    </div>

                    <div className="task-event__details">
                      <div className="task-event__inline">
                        {event.http_status_code != null && (
                          <span>
                            <span className="text-muted">HTTP </span>
                            <Badge
                              bg={event.http_status_code === 200 ? 'success' : 'danger'}
                              text={event.http_status_code === 200 ? undefined : 'white'}
                            >
                              {event.http_status_code}
                            </Badge>
                          </span>
                        )}
                        {event.duration_ms != null && (
                          <span className="text-muted">⏱ {formatMs(event.duration_ms)}</span>
                        )}
                        {event.via_proxy && (
                          <Badge style={{ backgroundColor: '#6c757d', color: '#fff' }}>via proxy</Badge>
                        )}
                        {event.backup_model && (
                          <span className="text-muted">model: <code className="task-detail__code">{event.backup_model}</code></span>
                        )}
                        {(event.tokens_prompt != null || event.tokens_completion != null) && (
                          <span className="text-muted">
                            tokens: {event.tokens_prompt ?? 0}p / {event.tokens_completion ?? 0}c
                          </span>
                        )}
                      </div>

                      {event.error_code && (
                        <div className="task-event__block">
                          <span className="text-muted">Error: </span>
                          <ErrorBadge>{event.error_code}</ErrorBadge>
                          {event.error_detail && (
                            <div className="text-muted small mt-1 task-detail__text-break">
                              {event.error_detail}
                            </div>
                          )}
                        </div>
                      )}

                      {event.oxylabs_session_id && (
                        <div className="task-event__block text-muted small">
                          session: <code className="task-detail__code">{event.oxylabs_session_id}</code>
                        </div>
                      )}

                      {event.vendor_request_id && (
                        <div className="task-event__block text-muted small">
                          req: <code className="task-detail__code">{event.vendor_request_id}</code>
                        </div>
                      )}

                      {event.metadata && (
                        <div className="task-event__block">
                          <CollapsibleJsonDetail label="meta" value={event.metadata} />
                        </div>
                      )}

                      {event.response_body_preview && (
                        <div className="task-event__block">
                          <CollapsibleJsonDetail label="preview" value={event.response_body_preview} />
                        </div>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  if (!task) return null;

  return (
    <div className="task-detail">
      <div className="d-flex align-items-center gap-3 mb-4">
        <Button variant="outline-secondary" size="sm" onClick={() => navigate('/tasks')}>
          <FontAwesomeIcon icon={faArrowLeft} className="me-1" /> Back
        </Button>
        <h2 className="mb-0">Task Detail</h2>
      </div>

      {renderTaskInfo()}
      {renderRecognitionResult()}
      {renderUploadedImages()}
      {renderClientDeviceSection()}
      {renderResultDiagnostics()}
      {renderErrorSection()}

      <Card>
        <Card.Header>
          <strong>Events Timeline</strong>
          <span className="ms-2 text-muted small">({events.length} events)</span>
        </Card.Header>
        <Card.Body>
          {renderIterations()}
          {renderEventsTimeline()}
        </Card.Body>
      </Card>
    </div>
  );
};

export default CoinTaskDetail;
